/**
 * TopbarMobileMenu — the account drawer (P25009-443 BR-1/BR-2/BR-4).
 *
 * THE DRAWER IS THE ACCOUNT RAIL. It renders the same groups, in the same
 * order, under the same names as the desktop rail — straight from
 * sellerNavGroups, the single source P25009-431 promoted. It used to hold the
 * avatar dropdown's items under the avatar dropdown's names
 * (Profilinställningar, Kontoinställningar), which meant a seller could not
 * reach Försäljning, Ekonomi or Förvaring from the menu at all.
 *
 * Structure (logged-in):
 *   ProfileCard (tappable avatar + name + "Visa profil →")
 *   Översikt
 *   ── Sälj / Order & pengar / Verkstad / Konto (from sellerNavGroups;
 *      view-only items carry a "view" note — BR-2's middle level)
 *   ── BLÄDDRA (Annonser, Bevakningar) — the public nav, last, where the
 *      desktop keeps it in the top bar
 *   ── FINNS PÅ DATORN — the desktop-only level, dimmed, with the
 *      open-on-a-computer note (BR-2/AC-9)
 *   ── HJÄLP, then demoted Logga ut
 *   ── Pinned footer: [+ Lägg in annons] + "eller skapa förfrågan →"
 *
 * Logged-out state keeps the sign-up/sign-in card + a trimmed
 * BLÄDDRA + HJÄLP subset.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';
import {
  mobileGroupsFor,
  mobileDesktopOnlyFor,
  MOBILE_LEVEL,
} from '../../../../components/SellerNav/sellerNavGroups';

import { Avatar, InlineTextButton, NamedLink } from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { Link } from 'react-router-dom/';

/* --- Lucide-style inline icons (stroke 1.5, 20×20). Inlined here so
 * the drawer has no external icon dependency; shape vocabulary matches
 * the desktop dropdown for visual consistency. */
const I = {
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  ),
  Bag: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
      <path d="M3 6h18M16 10a4 4 0 01-8 0" />
    </svg>
  ),
  File: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  ),
  Heart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  ),
  BarChart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 15l4-4 4 4 5-6" />
    </svg>
  ),
  Grid: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  HelpCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  /* Bevakningar — a bell reads as "tell me when this happens". */
  Bell: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </svg>
  ),
  Settings: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33 1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82 1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  CreditCard: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18M7 15h2" />
    </svg>
  ),
  Dollar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  Chat: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  LogOut: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Cart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5h2.2l2 10.2h10.2L19.6 8H6.4" />
      <circle cx="9" cy="19" r="1.3" />
      <circle cx="16.5" cy="19" r="1.3" />
    </svg>
  ),
  Box: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 8L12 3.5 3.5 8v8L12 20.5 20.5 16z" />
      <path d="M3.5 8l8.5 4.5L20.5 8M12 12.5v8" />
    </svg>
  ),
  Pin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s6.5-5.6 6.5-10.4A6.5 6.5 0 005.5 10.6C5.5 15.4 12 21 12 21z" />
      <circle cx="12" cy="10.3" r="2.3" />
    </svg>
  ),
  Store: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 9.5h16V20H4z" />
      <path d="M3.2 9.5L5 4.5h14l1.8 5" />
      <path d="M9.5 20v-6h5v6" />
    </svg>
  ),
  Wrench: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15.5 3.5a5.2 5.2 0 00-4.6 7.6L3.8 18.2a1.8 1.8 0 002.5 2.5l7.1-7.1a5.2 5.2 0 006.4-6.9l-2.9 2.9-2.5-.6-.6-2.5z" />
    </svg>
  ),
  Car: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 17v-4.2l2-5A2 2 0 016.9 6.5h10.2a2 2 0 011.9 1.3l2 5V17" />
      <path d="M3 12.8h18" />
      <circle cx="7.5" cy="17" r="1.5" />
      <circle cx="16.5" cy="17" r="1.5" />
    </svg>
  ),
  Chevron: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  ),
};

/* Menu row — icon · label · (badge or count or note or chevron). Tap-target
 * row height 48px per spec §5.4. Only one of badge/count renders;
 * active state replaces the right-side element with a 4px dot. `note` is the
 * BR-2 view-level marker ("view") — a fact about the row, so it wins over
 * the decorative chevron but never over a badge. */
const MenuRow = ({ icon, label, name, to, params, badge, count, note, isActive, onClick }) => {
  const showBadge = badge != null && badge > 0;
  const showCount = !showBadge && count != null && count > 0;
  const rightSlot = isActive ? (
    <span className={css.rowActiveDot} aria-hidden="true" />
  ) : showBadge ? (
    <span
      className={css.rowBadge}
      aria-label={`${badge > 99 ? '99+' : badge} olästa`}
    >
      {badge > 99 ? '99+' : badge}
    </span>
  ) : showCount ? (
    <span className={css.rowCount}>{count}</span>
  ) : note ? (
    <span className={css.rowNote}>{note}</span>
  ) : (
    <span className={css.rowChevron} aria-hidden="true">
      <I.Chevron />
    </span>
  );

  const inner = (
    <>
      <span className={css.rowIcon} aria-hidden="true">{icon}</span>
      <span className={css.rowLabel}>{label}</span>
      {rightSlot}
    </>
  );

  const className = classNames(css.row, isActive && css.rowActive);
  const ariaCurrent = isActive ? 'page' : undefined;

  if (to) {
    return (
      <Link to={to} className={className} aria-current={ariaCurrent} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  return (
    <NamedLink
      name={name}
      params={params}
      className={className}
      aria-current={ariaCurrent}
      onClick={onClick}
    >
      {inner}
    </NamedLink>
  );
};

const MenuGroup = ({ label, children }) => (
  <div className={css.group}>
    <div className={css.groupLabel}>{label}</div>
    {children}
  </div>
);

/* A desktop-only row (BR-2's third level): dimmed and NOT a link. Rendering
 * these as links would send a phone user to a bulk-upload table that cannot
 * work there; hiding them entirely would leave sellers wondering where the
 * page went. Present, named, and honest about needing a computer. */
const DimmedRow = ({ icon, label }) => (
  <div className={css.rowDimmed} aria-disabled="true">
    <span className={css.rowIcon} aria-hidden="true">{icon}</span>
    <span className={css.rowLabel}>{label}</span>
  </div>
);

/* Icon per rail item id. The rail itself is icon-free (desktop draws text);
 * the drawer draws icons, so the mapping lives here rather than in the shared
 * group source. An unmapped id gets the generic file icon rather than nothing. */
const iconForItem = id =>
  ({
    listings: <I.File />,
    packages: <I.Box />,
    bulk: <I.Grid />,
    sales: <I.Bag />,
    freight: <I.Box />,
    purchases: <I.Cart />,
    messages: <I.Mail />,
    finance: <I.Dollar />,
    subscription: <I.CreditCard />,
    statistics: <I.BarChart />,
    services: <I.Wrench />,
    storage: <I.Box />,
    addresses: <I.Pin />,
    shopProfile: <I.Store />,
    payouts: <I.Dollar />,
    settings: <I.Settings />,
    favourites: <I.Heart />,
  }[id] || <I.File />);

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const displayName = user.attributes.profile.displayName || user.attributes.profile.firstName || '';

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage;
  };

  /* The rail's groups, filtered for this seller — the same call SellerNav
   * makes, so the drawer and the desktop rail cannot disagree (BR-1). Both
   * Försäljning and Inköp are rows now; the old drawer showed one OR the
   * other depending on a buyer/seller mode, which is the mode-picking the
   * spec retires. */
  const accountGroups = mobileGroupsFor(user);
  const desktopOnlyItems = mobileDesktopOnlyFor(user);

  /* ============================================================
   * Logged-out — sign-up card + trimmed BLÄDDRA/HJÄLP groups.
   * ============================================================ */
  if (!isAuthenticated) {
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authCard}>
            <div className={css.authCardTitle}>Logga in för att komma åt ditt konto</div>
            <div className={css.authCardActions}>
              <NamedLink name="LoginPage" className={css.authCardPrimary}>
                <FormattedMessage id="TopbarMobileMenu.loginLink" />
              </NamedLink>
              <NamedLink name="SignupPage" className={css.authCardSecondary}>
                eller skapa konto
                <I.ArrowRight />
              </NamedLink>
            </div>
          </div>

          <MenuGroup label="BLÄDDRA">
            <MenuRow icon={<I.Grid />} label="Annonser" name="SearchPage" isActive={currentPageClass('SearchPage')} />
            <MenuRow icon={<I.Bell />} label="Bevakningar" name="SavedSearchesPage" isActive={currentPageClass('SavedSearchesPage')} />
          </MenuGroup>

          <MenuGroup label="HJÄLP">
            <MenuRow icon={<I.Chat />} label="Kontakt" to="/p/kontakt" />
          </MenuGroup>
        </div>

        <div className={css.footer}>
          <NamedLink className={css.footerPrimary} name="WheelplaceCreateListingPage">
            + Lägg in annons
          </NamedLink>
        </div>
      </div>
    );
  }

  /* ============================================================
   * Logged-in — full profile card + four groups + demoted logout.
   * ============================================================ */
  return (
    <div className={css.root}>
      <div className={css.content}>
        {/* Profile card — tappable header routing to profile settings. */}
        <NamedLink name="ProfileSettingsPage" className={css.profileCard}>
          <Avatar className={css.profileAvatar} user={currentUser} disableProfileLink />
          <span className={css.profileText}>
            <span className={css.profileName}>{displayName || 'Konto'}</span>
            <span className={css.profileLink}>
              Visa profil
              <I.ArrowRight />
            </span>
          </span>
        </NamedLink>

        {/* Home first, above the areas — same as the desktop rail. */}
        <MenuRow
          icon={<I.Grid />}
          label={<FormattedMessage id="SellerNav.overview" />}
          name="SellerDashboardPage"
          isActive={currentPageClass('SellerDashboardPage')}
        />

        {/* BR-1: the account rail's groups, order and names, verbatim from
          * sellerNavGroups. View-level items carry the "view" note (BR-2). */}
        {accountGroups.map(group => (
          <MenuGroup key={group.id} label={<FormattedMessage id={group.labelKey} />}>
            {group.items.map(item => (
              <MenuRow
                key={item.id}
                icon={iconForItem(item.id)}
                label={<FormattedMessage id={item.labelKey} />}
                name={item.route}
                params={item.routeParams}
                badge={item.id === 'messages' ? notificationCount : undefined}
                note={item.mobileLevel === MOBILE_LEVEL.VIEW ? 'view' : undefined}
                isActive={currentPageClass(item.route)}
              />
            ))}
            {/* Dina bilar rides in Konto, drawer-only: the desktop rail keeps
              * it off (431 decision — a saved-things page behind the car
              * icon), but on a phone the drawer IS the reachable surface and
              * BR-4 lists it as core. */}
            {group.id === 'account' ? (
              <MenuRow
                icon={<I.Car />}
                label="Dina bilar"
                name="SavedQueriesPage"
                isActive={currentPageClass('SavedQueriesPage')}
              />
            ) : null}
          </MenuGroup>
        ))}

        {/* The public nav, last — on desktop it lives in the top bar. */}
        <MenuGroup label="BLÄDDRA">
          <MenuRow
            icon={<I.Grid />}
            label="Annonser"
            name="SearchPage"
            isActive={currentPageClass('SearchPage')}
          />
          <MenuRow
            icon={<I.Bell />}
            label="Bevakningar"
            name="SavedSearchesPage"
            isActive={currentPageClass('SavedSearchesPage')}
          />
        </MenuGroup>

        {/* BR-2/AC-9: the desktop-only level — dimmed, collected, explained.
          * Rendered only when this seller actually has such items. */}
        {desktopOnlyItems.length ? (
          <MenuGroup label="FINNS PÅ DATORN">
            {desktopOnlyItems.map(item => (
              <DimmedRow
                key={item.id}
                icon={iconForItem(item.id)}
                label={<FormattedMessage id={item.labelKey} />}
              />
            ))}
            <p className={css.desktopOnlyNote}>
              Kräver filer, breda tabeller eller avtal. Öppna wheelplace.com på en dator.
            </p>
          </MenuGroup>
        ) : null}

        <MenuGroup label="HJÄLP">
          <MenuRow icon={<I.Chat />} label="Kontakt" to="/p/kontakt" />
        </MenuGroup>

        {/* Demoted logout — muted, no chevron, visually separated from
          * the account groups above. Confirmation dialog is a Phase 2
          * polish item (needs Modal threading from Topbar). */}
        <div className={css.divider} />
        <InlineTextButton rootClassName={css.logoutRow} onClick={onLogout}>
          <span className={css.rowIcon} aria-hidden="true"><I.LogOut /></span>
          <span className={css.rowLabel}>Logga ut</span>
        </InlineTextButton>
      </div>

      {/* Pinned footer — one primary CTA + secondary text link.
        * Respects iOS home-indicator safe area. */}
      <div className={css.footer}>
        <NamedLink className={css.footerPrimary} name="WheelplaceCreateListingPage">
          + Lägg in annons
        </NamedLink>
        <NamedLink
          className={css.footerSecondary}
          name="EditListingPage"
          params={{
            slug: 'draft',
            id: '00000000-0000-0000-0000-000000000000',
            type: 'new',
            tab: 'details',
          }}
          to={{ search: `listingType=buying` }}
        >
          eller skapa förfrågan
          <I.ArrowRight />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
